/* diagnostics.js — Contrôles indépendants des profils. N'émet que info et
 * warning, sauf un défaut d'intégrité constaté dans la structure. Libellés et
 * gravités viennent de findings.js. Constat : { level, code, message, scope,
 * subject, observed, character, confidence?, proof? }. */
'use strict';

import K from './findings.js';


// Combinaisons conteneur→codecs « attendus » (heuristique, non exhaustif).
var EXPECTED = {
  'Wave': ['PCM', 'PCM (non compressé)'],
  'FLAC': ['FLAC'],
  'MPEG Audio': ['MPEG Audio'],
  'WebM': ['VP8', 'VP9', 'AV1', 'Opus', 'Vorbis'],
  'MPEG-4': ['AVC', 'HEVC', 'MPEG-4 Visual', 'AV1', 'AAC', 'AC-3', 'E-AC-3', 'ALAC', 'MPEG Audio', 'DTS'],
  'Matroska': null // Matroska accepte presque tout : pas de contrôle
};

var LOW_BPP = 0.015, HIGH_BPP = 0.6;

function each(model, kind) {
  return model.tracks.filter(function (t) { return t.kind === kind; });
}

// `structure` : exploration complète si elle existe, sinon model.survey.
function diagnose(model, structure) {
  var out = [];
  function add(code, params, scope, subject, observed, proof) {
    var c = K.CATALOG[code];
    var d = {
      level: c.severity, code: code, message: K.message(code, params), scope: scope || null,
      subject: subject, observed: observed, character: c.character
    };
    var conf = K.confidence(code, params);
    if (conf) d.confidence = conf;
    if (proof) d.proof = proof;
    out.push(d);
  }
  function v(t, property, value) { return K.value(K.subjectOf(t), property, value); }
  var FILE = { kind: 'container' };
  var FILE_SUBJECT = FILE;

  var vids = each(model, 'video');
  var auds = each(model, 'audio');
  var subs = each(model, 'text');

  // Écart de durée audio/vidéo
  if (vids.length && auds.length) {
    var dv = vids[0].duration, da = auds[0].duration;
    if (dv != null && da != null) {
      var diff = Math.abs(dv - da), ref = Math.max(dv, da);
      if (diff > 0.5 && diff / ref > 0.02) {
        add('av-duration', { video: fmtS(dv), audio: fmtS(da) }, 'container', FILE,
          [v(vids[0], 'duration', dv), v(auds[0], 'duration', da)]);
      }
    }
  }

  // Un fichier audio à une seule piste n'a pas de langue à choisir.
  var audioOnly = !vids.length && auds.length === 1;
  [audioOnly ? [] : auds, subs].forEach(function (list) {
    list.forEach(function (t) {
      if (!t.language || t.language.code == null) {
        add('no-lang', { track: K.trackName(t, list.length) }, t.kind, K.subjectOf(t),
          [v(t, 'language.code', t.language ? t.language.raw : null)]);
      }
    });
  });

  // Plusieurs pistes audio « par défaut »
  var defAudio = auds.filter(function (a) { return a.default === true; });
  if (defAudio.length > 1) {
    add('multi-default', { count: defAudio.length }, 'audio', FILE,
      defAudio.map(function (a) { return v(a, 'default', true); }));
  }

  // Combinaison conteneur / codec inhabituelle
  var craw = model.container.formatRaw;
  if (EXPECTED.hasOwnProperty(craw) && EXPECTED[craw]) {
    var allowed = EXPECTED[craw];
    model.tracks.forEach(function (t) {
      if (t.kind !== 'video' && t.kind !== 'audio') return;
      if (t.formatRaw && allowed.indexOf(t.formatRaw) === -1) {
        add('unusual-combo', { codec: t.formatRaw, container: craw }, t.kind, K.subjectOf(t),
          [v(model.container, 'formatRaw', craw), v(t, 'formatRaw', t.formatRaw)]);
      }
    });
  }

  // Débit vidéo anormal (bits par pixel et par image)
  vids.forEach(function (t) {
    if (t.bitRate && t.width && t.height && t.frameRate) {
      var bpp = t.bitRate / (t.width * t.height * t.frameRate);
      var obs = [v(t, 'bitRate', t.bitRate), v(t, 'width', t.width), v(t, 'height', t.height), v(t, 'frameRate', t.frameRate)];
      if (bpp < LOW_BPP) add('low-bitrate', { bpp: fmtN(bpp, 3), threshold: fmtN(LOW_BPP, 3) }, 'video', K.subjectOf(t), obs);
      else if (bpp > HIGH_BPP) add('high-bitrate', { bpp: fmtN(bpp, 3), threshold: fmtN(HIGH_BPP, 1) }, 'video', K.subjectOf(t), obs);
    }
  });

  // HDR déclaré mais profondeur insuffisante
  vids.forEach(function (t) {
    if (t.hdrFormat && t.bitDepth != null && t.bitDepth < 10) {
      add('hdr-bitdepth', { hdr: t.hdrFormat, depth: t.bitDepth }, 'video', K.subjectOf(t),
        [v(t, 'hdrFormat', t.hdrFormat), v(t, 'bitDepth', t.bitDepth)]);
    }
  });

  // Disposition de canaux non déclarée
  auds.forEach(function (t) {
    if (t.channels != null && t.channels > 2 && (!t.channelLayout || t.channelLayout.raw == null)) {
      add('no-layout', { channels: t.channels }, 'audio', K.subjectOf(t),
        [v(t, 'channels', t.channels), v(t, 'channelLayout.raw', null)]);
    }
  });

  // Ratio d’affichage incohérent avec la définition (sans PAR explicite)
  vids.forEach(function (t) {
    if (t.displayAspectRatio && t.width && t.height) {
      var geo = t.width / t.height;
      if (Math.abs(geo - t.displayAspectRatio) / t.displayAspectRatio > 0.05 &&
          (t.pixelAspectRatio == null || Math.abs(t.pixelAspectRatio - 1) < 0.01)) {
        add('dar-mismatch', { dar: t.displayAspectRatio.toFixed(3), width: t.width, height: t.height, geometry: geo.toFixed(3) },
          'video', K.subjectOf(t),
          [v(t, 'displayAspectRatio', t.displayAspectRatio), v(t, 'width', t.width), v(t, 'height', t.height), v(t, 'pixelAspectRatio', t.pixelAspectRatio)]);
      }
    }
  });

  pictureFindings(model, add, v);

  structureFindings(model, structure, add);

  // Métadonnées personnelles (position, appareil, auteur, commentaire)
  var personal = (model.privacy || []).filter(function (p) { return K.PRIVACY_PERSONAL.indexOf(p.kind) >= 0; });
  if (personal.length) {
    var kinds = [];
    personal.forEach(function (p) { var l = K.PRIVACY_KINDS[p.kind].toLowerCase(); if (kinds.indexOf(l) < 0) kinds.push(l); });
    add('personal-metadata', { list: kinds.join(', ') }, 'container', FILE_SUBJECT,
      personal.map(function (p) { return K.value(FILE_SUBJECT, 'privacy.' + p.kind, p.value); }));
  }

  // erreurs, avertissements, informations ; ordre d'apparition sinon
  var rank = { error: 0, warning: 1, info: 2 };
  out.sort(function (a, b) { return rank[a.level] - rank[b.level]; });
  return out;
}

// Ne repose que sur des valeurs mesurées : muet tant que rien ne l'a été.
function pictureFindings(model, add, v) {
  function measuredOf(subject, property) {
    var list = model.measured || [];
    for (var i = 0; i < list.length; i++) {
      var m = list[i];
      if (m.property === property && m.subject.kind === subject.kind && m.subject.ordinal === subject.ordinal) return m;
    }
    return null;
  }
  each(model, 'video').forEach(function (t) {
    var subject = K.subjectOf(t);
    var scan = measuredOf(subject, 'scanType');
    var tele = measuredOf(subject, 'telecine');
    if (tele && tele.value === true) {
      // le télécinéma se corrige autrement qu'un entrelacement ordinaire
      add('telecine-content', {}, 'video', subject, [tele, v(t, 'scanType', t.scanType)]);
    } else if (scan && scan.value === 'interlaced' && t.scanType !== 'interlaced') {
      add('interlaced-content', {}, 'video', subject, [scan, v(t, 'scanType', t.scanType)]);
    }
    // Un cadre posé deux fois : du noir sur les quatre côtés à la fois.
    var aw = measuredOf(subject, 'activeWidth'), ah = measuredOf(subject, 'activeHeight');
    if (aw && ah && t.width && t.height && aw.value < t.width - 3 && ah.value < t.height - 3) {
      add('bars-both-axes', { width: aw.value, height: ah.value, frameWidth: t.width, frameHeight: t.height },
        'video', subject, [aw, ah, v(t, 'width', t.width), v(t, 'height', t.height)]);
    }
  });
}

// Intégrité et structure du conteneur, en valeurs mesurées.
function structureFindings(model, structure, add) {
  var FILE = { kind: 'container' };
  function measured(property, value) {
    return { subject: FILE, property: property, value: value, origin: 'measured', scope: 'file', source: 'structure' };
  }
  function at(a) {
    return {
      params: { offset: a.offset.toLocaleString('fr-FR'), element: a.name ? K.fill(K.LABELS.elementSuffix, { name: a.name }) : '' },
      proof: { structure: { name: a.name, offset: a.offset } }
    };
  }
  var truncatedAt = null, invalid = [];
  if (structure && structure.supported) {
    structure.anomalies.forEach(function (a) {
      if (a.kind === 'truncated' && !truncatedAt) truncatedAt = a;
      else if (a.kind === 'invalid') invalid.push(a);
    });
  } else if (model.survey) {
    truncatedAt = model.survey.truncatedAt;
    if (model.survey.invalidAt) invalid.push(model.survey.invalidAt);
  }
  if (truncatedAt) {
    var t = at(truncatedAt);
    add('truncated', t.params, 'container', FILE, [measured('truncated', true)], t.proof);
  }
  invalid.forEach(function (a) {
    var x = at(a);
    add('invalid-structure', x.params, 'container', FILE, [], x.proof);
  });
  (model.measured || []).forEach(function (v) {
    if (v.property === 'moovPosition' && v.value === 'after-mdat') {
      add('moov-late', {}, 'container', FILE, [v], model.survey && model.survey.moov
        ? { structure: { name: 'moov', offset: model.survey.moov.offset } } : null);
    }
  });
}

function fmtS(s) { return (Math.round(s * 100) / 100).toLocaleString('fr-FR') + ' s'; }
function fmtN(n, d) { return n.toLocaleString('fr-FR', { maximumFractionDigits: d }); }

var api = { diagnose: diagnose };
globalThis.MediaDiagnostics = api;
export default api;
